// Hotel detail + room selection — one page, deliberately.
//
// A room offer means nothing away from the property it belongs to: the same
// "Deluxe King" is a different product at a different cancellation policy in
// every hotel. So the flow the brief asks for — search → details → select rooms
// → booking — puts details and room selection on one screen, with the gallery
// and policies above and the bookable offers below.
//
// Selecting a room posts /v1/hotels/booking/, which creates the neptune Booking
// row and quotes the offer. It reserves nothing: a hotel offer cannot be held,
// so the reservation happens inside /book/ at the end, atomically.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { loadHotelSearch } from "../lib/hotelSearchCache";
import {
  fmtBoard, fmtDateLong, fmtDeadline, fmtMoney, nightsLabel, occupancyLabel,
  reviewLabel, starLabel,
} from "../lib/hotelFormat";
import type { HotelDetailResponse, HotelItinerary, RoomOffer } from "../lib/hotelTypes";

export function HotelDetail() {
  const { hotelId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const cached = useMemo(() => loadHotelSearch(), []);
  const traceId = cached?.response.trace_id;

  const [gallery, setGallery] = useState(0);
  const [showAllFacilities, setShowAllFacilities] = useState(false);

  const detailQ = useQuery({
    queryKey: ["hotelDetail", traceId, hotelId],
    queryFn: () => api.post<HotelDetailResponse>("/v1/hotels/details/", {
      trace_id: traceId, hotel_id: hotelId,
    }),
    enabled: !!traceId && !!hotelId,
  });

  const createBooking = useMutation({
    mutationFn: (offerId: string) =>
      api.post<HotelItinerary>("/v1/hotels/booking/", {
        trace_id: traceId,
        hotel_id: hotelId,
        offer_id: offerId,
        // Pin explicitly. The backend can recover the provider from the trace
        // too, but naming it means a create never depends on a cache entry
        // still being alive.
        source: cached?.form.source ?? detailQ.data?.provider,
      }),
    onSuccess: (booking) => {
      // Seed the query cache so the guests page renders instantly rather than
      // flashing a loader for a round-trip we just made.
      qc.setQueryData(["hotelBooking", booking.booking_id], booking);
      nav(`/booking/${booking.booking_id}/guests`);
    },
  });

  useEffect(() => { window.scrollTo(0, 0); }, [hotelId]);

  if (!cached || !traceId) {
    return (
      <div className="container" style={{ padding: "60px 0" }}>
        <div className="app-error">
          No hotel search in this session. <a href="/hotels/">Start a new search</a>.
        </div>
      </div>
    );
  }
  if (detailQ.isLoading) return <HotelDetailSkeleton />;
  if (detailQ.error || !detailQ.data) {
    const message = detailQ.error instanceof ApiError
      ? detailQ.error.message
      : "Couldn't load this hotel.";
    return (
      <div className="container" style={{ padding: "40px 0" }}>
        <div className="app-error">{message}</div>
        <button type="button" className="btn btn-ghost btn-pill" style={{ marginTop: 14 }}
                onClick={() => nav("/results")}>
          ← Back to results
        </button>
      </div>
    );
  }

  const detail = detailQ.data;
  const hotel = detail.hotel;
  const currency = detail.currency;
  const review = reviewLabel(hotel);
  const stars = starLabel(hotel.star_rating);
  const images = hotel.images?.length ? hotel.images : [];
  const facilities = showAllFacilities ? hotel.facilities : hotel.facilities.slice(0, 10);
  const createError = createBooking.error instanceof ApiError ? createBooking.error.message : null;

  return (
    <>
      <section className="results-topbar">
        <div className="container topbar-inner">
          <button type="button" className="hotel-back" onClick={() => nav("/results")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 19l-7-7 7-7" /></svg>
            All stays
          </button>
          <div className="summary">
            <div className="when-pax">
              <b>{fmtDateLong(detail.check_in)}</b> → <b>{fmtDateLong(detail.check_out)}</b>
              &nbsp;·&nbsp; {nightsLabel(detail.nights)} · {occupancyLabel(detail.occupancies)}
            </div>
          </div>
          <button type="button" className="modify" onClick={() => nav("/")}>Modify search</button>
        </div>
      </section>

      <div className="container">
        <div className="hotel-detail-wrap">
          {/* ── gallery ─────────────────────────────────────────────────── */}
          {!!images.length && (
            <div className="hotel-gallery">
              <div className="hotel-gallery-main">
                <img src={images[gallery]?.url} alt={images[gallery]?.caption || hotel.name} />
                {images.length > 1 && (
                  <>
                    <button type="button" className="gal-nav prev" aria-label="Previous photo"
                            onClick={() => setGallery((i) => (i - 1 + images.length) % images.length)}>‹</button>
                    <button type="button" className="gal-nav next" aria-label="Next photo"
                            onClick={() => setGallery((i) => (i + 1) % images.length)}>›</button>
                    <span className="gal-count">{gallery + 1} / {images.length}</span>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="hotel-gallery-strip">
                  {images.slice(0, 8).map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`gal-thumb${i === gallery ? " active" : ""}`}
                      onClick={() => setGallery(i)}
                      aria-label={`Photo ${i + 1}`}
                    >
                      <img src={img.url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── header ──────────────────────────────────────────────────── */}
          <div className="hotel-detail-head">
            <div>
              <h1>{hotel.name}</h1>
              <div className="hotel-detail-loc">
                {stars && <span className="hotel-stars">{stars}</span>}
                {[hotel.address, hotel.city_name].filter(Boolean).join(" · ")}
              </div>
              {(hotel.check_in_time || hotel.check_out_time) && (
                <div className="hotel-times">
                  {hotel.check_in_time && <span>Check-in from <b>{hotel.check_in_time}</b></span>}
                  {hotel.check_out_time && <span>Check-out by <b>{hotel.check_out_time}</b></span>}
                </div>
              )}
            </div>
            {review ? (
              <div className="hotel-score lg">
                <span className="n">{review.score}</span>
                <span className="w">{review.word}</span>
                <span className="c">{review.count}</span>
              </div>
            ) : (
              <div className="hotel-score lg unrated">
                <span className="w">Unrated</span>
                {!!hotel.review_count && (
                  <span className="c">{hotel.review_count.toLocaleString("en-IN")} reviews</span>
                )}
              </div>
            )}
          </div>

          <div className="hotel-detail-grid">
            <div>
              {/* ── rooms ─────────────────────────────────────────────── */}
              <section className="hotel-rooms" id="rooms">
                <div className="hotel-section-head">
                  <h2>Choose your <span className="serif">room</span></h2>
                  <span className="hotel-section-sub">
                    {detail.rooms.length} {detail.rooms.length === 1 ? "option" : "options"} ·
                    prices are the total for {nightsLabel(detail.nights)}
                    {detail.occupancies.length > 1 ? `, all ${detail.occupancies.length} rooms` : ""}
                  </span>
                </div>

                {createError && (
                  <div className="app-error inline" style={{ marginBottom: 14 }}>{createError}</div>
                )}

                <div className="room-list">
                  {detail.rooms.map((room) => (
                    <RoomRow
                      key={room.offer_id}
                      room={room}
                      currency={currency}
                      nights={detail.nights}
                      roomsRequested={detail.occupancies.length}
                      busy={createBooking.isPending}
                      onSelect={() => createBooking.mutate(room.offer_id)}
                    />
                  ))}
                </div>
              </section>

              {/* ── about ─────────────────────────────────────────────── */}
              {hotel.description && (
                <section className="hotel-block">
                  <h3>About this <span className="serif">property</span></h3>
                  <div className="hotel-prose">
                    {hotel.description.split("\n").slice(0, 8).map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </section>
              )}

              {!!hotel.facilities.length && (
                <section className="hotel-block">
                  <h3>Facilities</h3>
                  <div className="hotel-facility-grid">
                    {facilities.map((f) => (
                      <span key={f} className="hotel-facility">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        {f}
                      </span>
                    ))}
                  </div>
                  {hotel.facilities.length > 10 && (
                    <button type="button" className="btn-link"
                            onClick={() => setShowAllFacilities((v) => !v)}>
                      {showAllFacilities
                        ? "Show fewer"
                        : `Show all ${hotel.facilities.length} facilities`}
                    </button>
                  )}
                </section>
              )}

              {!!hotel.policies?.length && (
                <section className="hotel-block">
                  <h3>Property <span className="serif">policies</span></h3>
                  {hotel.policies.map((policy, i) => (
                    <div className="hotel-policy" key={i}>
                      <b>{policy.name}</b>
                      <p>{policy.description}</p>
                    </div>
                  ))}
                </section>
              )}
            </div>

            {/* ── sidebar ──────────────────────────────────────────────── */}
            <aside className="hotel-detail-side">
              <div className="summary-card">
                <h3>Your <span className="serif">stay</span></h3>
                <div className="sub">{hotel.city_name || ""}</div>

                <div className="hotel-stay-dates">
                  <div>
                    <span className="lbl">Check-in</span>
                    <b>{fmtDateLong(detail.check_in)}</b>
                    {hotel.check_in_time && <span className="t">from {hotel.check_in_time}</span>}
                  </div>
                  <div className="hotel-stay-nights">{nightsLabel(detail.nights)}</div>
                  <div>
                    <span className="lbl">Check-out</span>
                    <b>{fmtDateLong(detail.check_out)}</b>
                    {hotel.check_out_time && <span className="t">by {hotel.check_out_time}</span>}
                  </div>
                </div>

                <div className="fare-list">
                  <div className="fare-line">
                    <span className="lbl">Guests</span>
                    <span>{occupancyLabel(detail.occupancies)}</span>
                  </div>
                  <div className="fare-line">
                    <span className="lbl">Rooms from</span>
                    <span className="amt">
                      {fmtMoney(detail.rooms[0]?.pricing.final_fare, currency)}
                    </span>
                  </div>
                </div>

                <a href="#rooms" className="btn btn-yellow btn-pill btn-full" style={{ marginTop: 18 }}>
                  Choose a room
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </a>
              </div>

              {hotel.sentiment && (hotel.sentiment.pros.length > 0 || hotel.sentiment.cons.length > 0) && (
                <div className="summary-card" style={{ marginTop: 16 }}>
                  <h3>What guests <span className="serif">say</span></h3>
                  {!!hotel.sentiment.pros.length && (
                    <div className="sentiment-list pros">
                      {hotel.sentiment.pros.slice(0, 4).map((p) => <span key={p}>+ {p}</span>)}
                    </div>
                  )}
                  {!!hotel.sentiment.cons.length && (
                    <div className="sentiment-list cons">
                      {hotel.sentiment.cons.slice(0, 3).map((c) => <span key={c}>− {c}</span>)}
                    </div>
                  )}
                  {!!hotel.sentiment.categories.length && (
                    <div className="sentiment-cats">
                      {hotel.sentiment.categories.slice(0, 5).map((cat) => (
                        <div className="sentiment-cat" key={cat.name}>
                          <span>{cat.name}</span>
                          <b>{cat.rating != null ? (cat.rating / 2).toFixed(1) : "—"}</b>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── one bookable room ──────────────────────────────────────────────────────

function RoomRow(props: {
  room: RoomOffer;
  currency: string;
  nights: number;
  roomsRequested: number;
  busy: boolean;
  onSelect: () => void;
}) {
  const { room } = props;
  const [open, setOpen] = useState(false);
  const perNight = room.pricing.per_night_fare;
  const sleeps = room.sleeps.adults + room.sleeps.children;

  return (
    // Many properties publish no per-room photography at all, so the image
    // column is dropped rather than left as an empty 168px gutter squeezing the
    // room name into a two-word-per-line column.
    <article className={`room-row${room.images.length ? "" : " no-img"}`}>
      {!!room.images.length && (
        <div className="room-img">
          <img src={room.images[0]} alt="" loading="lazy" />
        </div>
      )}

      <div className="room-body">
        <h4>{room.name}</h4>

        <div className="room-tags">
          <span className={`tag ${room.free_breakfast ? "refundable" : "subtle"}`}>
            {fmtBoard(room.board_basis, room.board_name)}
          </span>
          <span className={`tag ${room.refundable ? "refundable" : "non-refundable"}`}>
            {room.refundable ? "Free cancellation" : "Non-refundable"}
          </span>
          {!!sleeps && (
            <span className="tag subtle">
              Sleeps {sleeps}
              {room.rooms_count > 1 ? ` · ${room.rooms_count} rooms` : ""}
            </span>
          )}
          {!!room.room_size_sqm && <span className="tag subtle">{room.room_size_sqm} m²</span>}
        </div>

        {!!room.beds.length && (
          <div className="room-beds">
            {room.beds.map((bed, i) => (
              <span key={i}>
                {bed.quantity && bed.quantity > 1 ? `${bed.quantity} × ` : ""}{bed.type}
              </span>
            ))}
          </div>
        )}

        <div className={`room-cancel${room.refundable ? " ok" : ""}`}>
          {room.free_cancellation_until
            ? <>Free cancellation until <b>{fmtDeadline(room.free_cancellation_until)}</b></>
            : room.cancellation_summary}
        </div>

        {(!!room.amenities.length || room.rates.length > 1 || !!room.cancellation_policies.length) && (
          <button type="button" className="btn-link" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide details" : "Room details & policy"}
          </button>
        )}

        {open && (
          <div className="room-detail">
            {!!room.amenities.length && (
              <div className="room-amenities">
                {room.amenities.map((a) => <span key={a}>{a}</span>)}
              </div>
            )}

            {/* One line per occupancy. For a multi-room booking this is where a
                partner sees that the offer really does cover every room, and
                which head count each was priced at. */}
            {room.rates.length > 1 && (
              <div className="room-rates">
                {room.rates.map((rate) => (
                  <div className="room-rate" key={rate.rate_id}>
                    <span>
                      Room {rate.occupancy_number} · {rate.adults} adult{rate.adults === 1 ? "" : "s"}
                      {rate.children ? ` + ${rate.children} child${rate.children === 1 ? "" : "ren"}` : ""}
                    </span>
                    <b>{fmtMoney(rate.pricing.final_fare, props.currency)}</b>
                  </div>
                ))}
              </div>
            )}

            {!!room.cancellation_policies.length && (
              <div className="room-policies">
                {room.cancellation_policies.map((policy, i) => (
                  <div key={i}>
                    From <b>{fmtDeadline(policy.deadline)}</b> —{" "}
                    {policy.amount
                      ? <>fee of <b>{fmtMoney(policy.amount, policy.currency)}</b></>
                      : <b>no charge</b>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="room-price">
        {perNight != null && (
          <div className="per-night">{fmtMoney(perNight, props.currency)}<span> / night</span></div>
        )}
        <div className="total">{fmtMoney(room.pricing.final_fare, props.currency)}</div>
        <div className="total-sub">
          total · {nightsLabel(props.nights)}
          {props.roomsRequested > 1 ? ` · ${props.roomsRequested} rooms` : ""}
        </div>
        {!!room.pricing.taxes_at_property && (
          <div className="at-property" title={room.pricing.taxes_at_property_breakdown
            .map((t) => `${t.description}: ${fmtMoney(t.amount, t.currency)}`).join("\n")}>
            + {fmtMoney(room.pricing.taxes_at_property, props.currency)} at property
          </div>
        )}
        <button
          type="button"
          className={`btn btn-yellow btn-pill${props.busy ? " loading" : ""}`}
          disabled={props.busy}
          onClick={props.onSelect}
        >
          Select room
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
        </button>
      </div>
    </article>
  );
}

function HotelDetailSkeleton() {
  return (
    <div className="container">
      <div className="hotel-detail-wrap">
        <span className="skeleton" style={{ height: 320, width: "100%", borderRadius: 16 }} />
        <div style={{ marginTop: 20 }}>
          <span className="skeleton skeleton-line lg" style={{ width: 320 }} />
          <div style={{ marginTop: 10 }}>
            <span className="skeleton skeleton-line" style={{ width: 220 }} />
          </div>
        </div>
        <div style={{ marginTop: 28, display: "grid", gap: 14 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} className="skeleton" style={{ height: 150, width: "100%", borderRadius: 14 }} />
          ))}
        </div>
      </div>
    </div>
  );
}
